// Shopify variant metafield namespace + key that Redo owns for the HSA/FSA
// product. No code writes this yet — it exists as the single source of truth
// for the storefront app block (redo/shopify/app/extensions/theme-extension/
// blocks/hsa-pdp-block.liquid) and for a future `createHsaMetafieldDefinitions`
// setup function modeled on `createRedoExchangeMetafieldDefinitions` in
// redo/shopify/server/src/products-as-variants/configure-products-as-variants-metafields.ts.
//
// Value must be one of the `HsaEligibility` enum strings from
// ./hsa-eligibility.ts ("not_eligible" | "eligible" | "eligible_with_lmn").

import { hsaEligibility, HsaEligibility } from "./hsa-eligibility";

export const REDO_HSA_METAFIELD_NAMESPACE = "redohsa";

export const REDO_HSA_ELIGIBILITY_METAFIELD_KEY = "hsa_eligibility";

// Variant metafields written by the third-party GALE Payments app. Used as a
// fallback eligibility source for merchants who have GALE installed but have
// not yet authored Redo-side eligibility rules. The contract mirrors GALE's
// own storefront widget: `gale_eligibility` is a "1"/"0" bool that gates
// everything, and `gale_product_type` (`dual_purpose` | `medical_mcc` | empty)
// distinguishes the LMN path from plain eligibility.
export const GALE_PAYMENTS_METAFIELD_NAMESPACE = "gale_payments";
export const GALE_PAYMENTS_ELIGIBILITY_METAFIELD_KEY = "gale_eligibility";
export const GALE_PAYMENTS_PRODUCT_TYPE_METAFIELD_KEY = "gale_product_type";

export const GALE_DUAL_PURPOSE_PRODUCT_TYPE = "dual_purpose";

// GALE's widget logic, verbatim: the `gale_eligibility` bool gates
// eligibility outright; when it's set, `gale_product_type` picks the flavor —
// `dual_purpose` requires a health assessment (ELIGIBLE_WITH_LMN), anything
// else (`medical_mcc`, unknown, or absent) is plain ELIGIBLE. GALE's widget
// only ever checks `"1"`, but we tolerate `"true"`/`"false"` spellings too.
export function mapGaleMetafieldsToHsaEligibility(
  eligibilityValue: string | null,
  productTypeValue: string | null,
): HsaEligibility | null {
  if (eligibilityValue === "1" || eligibilityValue === "true") {
    return productTypeValue === GALE_DUAL_PURPOSE_PRODUCT_TYPE
      ? HsaEligibility.ELIGIBLE_WITH_LMN
      : HsaEligibility.ELIGIBLE;
  }
  if (eligibilityValue === "0" || eligibilityValue === "false") {
    return HsaEligibility.NOT_ELIGIBLE;
  }
  return null;
}

// Metafield-only variant of the two-tier eligibility rule in
// redo/conversion/hsa/service/src/resolve-variant-hsa-eligibility.ts, for
// surfaces that can't reach the CRDB canonical store (e.g. checkout UI
// extensions): a valid `redohsa.hsa_eligibility` mirror value wins; otherwise
// the GALE metafields are mapped; otherwise NOT_ELIGIBLE.
export function resolveHsaEligibilityFromMetafields(values: {
  redoValue: string | null;
  galeEligibilityValue: string | null;
  galeProductTypeValue: string | null;
}): HsaEligibility {
  return resolveHsaEligibilityWithSource(values).eligibility;
}

export type HsaEligibilityResolutionSource = "redo" | "gale" | "default";

export function resolveHsaEligibilityWithSource(values: {
  redoValue: string | null;
  galeEligibilityValue: string | null;
  galeProductTypeValue: string | null;
}): { eligibility: HsaEligibility; source: HsaEligibilityResolutionSource } {
  if (values.redoValue !== null) {
    const parsed = hsaEligibility.safeParse(values.redoValue);
    if (parsed.success) {
      return { eligibility: parsed.data, source: "redo" };
    }
  }
  const galeEligibility = mapGaleMetafieldsToHsaEligibility(
    values.galeEligibilityValue,
    values.galeProductTypeValue,
  );
  if (galeEligibility !== null) {
    return { eligibility: galeEligibility, source: "gale" };
  }
  return { eligibility: HsaEligibility.NOT_ELIGIBLE, source: "default" };
}
